import { useEffect, useRef } from 'react';

import './SoundWave.css';

export default function SoundWave(props) {
    const waveCanvas = useRef(0);

    useEffect(() => {
        // Prepare to draw
        const canvas = waveCanvas.current;
        const width = canvas.width;
        const height = canvas.height;
        const lineMaxHeight = height * 2/3;
        const canvasCtx = canvas.getContext('2d');

        function clearCanvas() {
            canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
        }

        function drawWave(wave) { // Takes an array of floats between -1 and 1
            let spacing = width / (wave.length + 1);

            wave.forEach((value, valueI) => {
                value = Math.max(value, 0.01);
                canvasCtx.beginPath();
                canvasCtx.moveTo(spacing * (valueI + 1), height / 2 - value * lineMaxHeight / 2); // Go to top of line
                canvasCtx.lineTo(spacing * (valueI + 1), height / 2 + value * lineMaxHeight / 2); // Line to bottom of line
                canvasCtx.lineWidth = 2;
                canvasCtx.lineCap = 'round';
                canvasCtx.strokeStyle = props.color ?? 'black';
                canvasCtx.stroke();
            });
        }

        let wave = new Array(300).fill(0);
        clearCanvas();
        drawWave(wave);

        fetch(props.waveDataUrl)
            .then(response => response.json())
            .then((waveData) => {
                const video = props.vid.current;
                const sampleInt = waveData.sampleInterval;

                let pauseStartT = null;
                let pauseEndT = null;
                let pauseEndTransitionTime;
                function redraw() {
                    let currTime = video.currentTime;
                    let ind = Math.floor(currTime / sampleInt);
                    let paused = video.paused;

                    // Remember the time when paused
                    if (paused && !pauseStartT) {
                        pauseStartT = new Date();
                        pauseEndT = null;
                    } else if (!paused && !pauseEndT) {
                        pauseStartT = null;
                        pauseEndT = new Date();
                        pauseEndTransitionTime = currTime % sampleInt;
                    }

                    let timeSincePauseEnd = (new Date() - pauseEndT) / 1000; // Only used when appropriate
                    let prevWave = (props.stopWhenPaused && !paused && (timeSincePauseEnd < pauseEndTransitionTime)) ? new Array(waveData.numLines).fill(0) : waveData.data[Math.max(ind - 1, 0)]; // All 0 if currently transitioning back up
                    let currWave = (props.stopWhenPaused && paused) ? new Array(waveData.numLines).fill(0) : waveData.data[ind]; // All 0 if paused
                    let interpolationT;
                    if (props.stopWhenPaused && paused) {
                        interpolationT = Math.min(((new Date() - pauseStartT) / 1000) / sampleInt, 1);
                    } else if (props.stopWhenPaused && !paused && timeSincePauseEnd < pauseEndTransitionTime) {
                        interpolationT = timeSincePauseEnd / pauseEndTransitionTime;
                    } else {
                        interpolationT = (currTime - ind * sampleInt) / sampleInt;
                    }
                    
                    let interpolated = [];
                    for (let i = 0; i < currWave.length; i++) {
                        interpolated.push(easeOutCubic(prevWave[i], currWave[i], interpolationT));
                    }

                    clearCanvas();
                    drawWave(interpolated);

                    requestAnimationFrame(() => {
                        redraw();
                    });
                }

                redraw();
            });
    }, [])

    return (
        <div className="sound-wave">
            <canvas ref={waveCanvas} width="1500" height="150"></canvas>
        </div>
    )
}

function lerp(a, b, t) {
    return t * (b - a) + a
}

function easeOutCubic(a, b, t) {
    return lerp(a, b, 1 - Math.pow(1 - t, 3));
}